import { Building2, Check, ChevronDown, ChevronRight, Globe, Search } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useAdministrationScopes } from './AdministrationScopesProvider';
import {
  placeBasePath,
  samePlace,
  type AdministrationScope,
  type Place,
  type PlaceCan,
} from './place';
import { placeEntryPath } from './scopes';
import {
  usePlacePage,
  type PageRead,
  type PlacePageState,
  type PlaceSummary,
} from './use-place-page';

/** Name contains, ignoring case and accents — the same rule the server applies to `q`. */
function matches(name: string, q: string): boolean {
  const fold = (value: string) =>
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLocaleLowerCase();
  return q === '' || fold(name).includes(fold(q));
}

type RowProps = {
  readonly icon: ReactNode;
  readonly name: string;
  readonly sub: string;
  readonly href: string;
  readonly current: boolean;
  readonly depth?: number;
  readonly onChosen?: (() => void) | undefined;
  readonly trailing?: ReactNode;
};

function Row({ icon, name, sub, href, current, depth = 0, onChosen, trailing }: RowProps) {
  const { t } = useTranslation('administration');
  return (
    <li className={cn('relative flex items-center gap-1', depth > 0 && 'pl-7')}>
      {depth > 0 ? (
        <span
          aria-hidden="true"
          className="border-chip-border absolute top-0 bottom-0 left-3 border-l"
        />
      ) : null}
      <Link
        to={href}
        onClick={onChosen}
        aria-current={current ? 'page' : undefined}
        className={cn(
          'flex min-h-12 min-w-0 flex-1 items-center gap-3 rounded-md px-3 py-1 no-underline',
          current ? 'bg-selected text-selected-foreground' : 'text-foreground hover:bg-accent',
        )}
      >
        <span
          aria-hidden="true"
          className={cn('flex', current ? 'text-selected-foreground' : 'text-muted-foreground')}
        >
          {icon}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn('truncate text-[14.5px]', (current || depth === 0) && 'font-semibold')}
          >
            {name}
          </span>
          <span className="text-muted-foreground truncate text-[12.5px]">
            {sub}
            {current ? ` · ${t('picker.current')}` : ''}
          </span>
        </span>
        {current ? (
          <Check aria-hidden="true" className="text-selected-foreground size-4.5 shrink-0" />
        ) : null}
      </Link>
      {trailing}
    </li>
  );
}

/** What a row of the chooser shows while searching: decided, or still being read. */
type Shown = 'shown' | 'hidden' | 'pending';

function OrganizationsList({
  state,
  more,
  retry,
  searching,
  current,
  onChosen,
}: {
  readonly state: PlacePageState;
  readonly more: () => void;
  readonly retry: () => void;
  readonly searching: boolean;
  readonly current: Place | null;
  readonly onChosen?: (() => void) | undefined;
}) {
  const { t } = useTranslation('administration');
  // While searching, a tenant shown for its own name may have no organization that matches:
  // that is not "no active organization", so nothing is said under it.
  if (searching && state.status === 'ready' && state.items.length === 0) {
    return null;
  }
  return (
    <ul className="flex flex-col gap-0.5" aria-busy={state.status === 'loading'}>
      {state.items.map((organization) => (
        <Row
          key={organization.id}
          icon={<Building2 className="size-4.5" />}
          name={organization.name}
          sub={t('picker.organizationSub')}
          href={placeBasePath({ kind: 'organization', id: organization.id })}
          current={
            current !== null && samePlace(current, { kind: 'organization', id: organization.id })
          }
          depth={1}
          onChosen={onChosen}
        />
      ))}
      <PageFooter
        state={state}
        more={more}
        retry={retry}
        emptyKey="picker.noOrganizations"
        depth={1}
      />
    </ul>
  );
}

function PageFooter({
  state,
  more,
  retry,
  emptyKey,
  depth = 0,
}: {
  readonly state: ReturnType<typeof usePlacePage>['state'];
  readonly more: () => void;
  readonly retry: () => void;
  /** What an empty list says; none for the platform's tenants, whose search the chooser says. */
  readonly emptyKey: 'picker.noOrganizations' | null;
  readonly depth?: number;
}) {
  const { t } = useTranslation('administration');
  const pad = depth > 0 ? 'pl-10' : 'pl-3';
  if (state.status === 'loading') {
    return (
      <li role="status" className={cn('text-muted-foreground py-2 text-[13px]', pad)}>
        {t('picker.loading')}
      </li>
    );
  }
  if (state.status === 'error') {
    return (
      <li role="alert" className={cn('flex items-center gap-3 py-2 text-[13px]', pad)}>
        {t('picker.loadError')}
        <Button variant="outline" size="sm" onClick={retry}>
          {t('picker.retry')}
        </Button>
      </li>
    );
  }
  if (state.items.length === 0) {
    return emptyKey === null ? null : (
      <li className={cn('text-muted-foreground py-2 text-[13px]', pad)}>{t(emptyKey)}</li>
    );
  }
  if (state.hasMore) {
    return (
      <li className={cn('py-1', pad)}>
        <Button variant="ghost" size="sm" onClick={more}>
          {t('picker.more')}
        </Button>
      </li>
    );
  }
  return null;
}

type PageState = ReturnType<typeof usePlacePage>['state'];

/** A tenant shows when it is not filtered out by its own name, or something below it shows. */
function tenantShown(filteredOut: boolean, state: PageState): Shown {
  if (!filteredOut || state.items.length > 0 || state.status === 'error') return 'shown';
  return state.status === 'loading' ? 'pending' : 'hidden';
}

function platformShown(state: PageState): Shown {
  if (state.status === 'error') return 'shown';
  return state.status === 'loading' && state.items.length === 0 ? 'pending' : 'hidden';
}

function TenantToggle({
  tenantName,
  open,
  listId,
  onToggle,
}: {
  readonly tenantName: string;
  readonly open: boolean;
  readonly listId: string;
  readonly onToggle: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-expanded={open}
      aria-controls={listId}
      aria-label={t(open ? 'picker.collapse' : 'picker.expand', { tenant: tenantName })}
      onClick={onToggle}
    >
      {open ? <ChevronDown /> : <ChevronRight />}
    </Button>
  );
}

function TenantRow({
  tenant,
  can,
  q,
  current,
  onChosen,
  defaultOpen,
  onShown,
}: {
  readonly tenant: { readonly id: string; readonly name: string };
  readonly can: PlaceCan;
  readonly q: string;
  readonly current: Place | null;
  readonly onChosen?: (() => void) | undefined;
  readonly defaultOpen: boolean;
  readonly onShown: (key: string, shown: Shown | null) => void;
}) {
  const { t } = useTranslation('administration');
  const [open, setOpen] = useState(defaultOpen);
  const listId = useId();
  const tenantId = tenant.id;
  const searching = q !== '';
  // While searching, the tenant's organizations are read with the search, open or not: the tenant
  // is shown when its own name matches, or when one of its organizations does.
  const expanded = open || searching;
  const read = useCallback<PageRead>(
    (query) =>
      apiClient.GET('/v1/administration/tenants/{tenantId}/organizations', {
        params: { path: { tenantId }, query },
      }),
    [tenantId],
  );
  const organizations = usePlacePage(expanded ? read : null, q);
  const { state } = organizations;
  const shown = tenantShown(searching && !matches(tenant.name, q), state);
  useEffect(() => {
    onShown(`tenant:${tenantId}`, shown);
  }, [onShown, tenantId, shown]);
  useEffect(() => () => onShown(`tenant:${tenantId}`, null), [onShown, tenantId]);

  if (shown !== 'shown') {
    return null;
  }
  return (
    <>
      <Row
        icon={<Building2 className="size-4.5" />}
        name={tenant.name}
        sub={t('picker.tenantSub')}
        href={placeEntryPath({ kind: 'tenant', id: tenant.id }, can)}
        current={current !== null && samePlace(current, { kind: 'tenant', id: tenant.id })}
        onChosen={onChosen}
        trailing={
          searching ? undefined : (
            <TenantToggle
              tenantName={tenant.name}
              open={open}
              listId={listId}
              onToggle={() => setOpen((value) => !value)}
            />
          )
        }
      />
      {expanded ? (
        <li id={listId}>
          <OrganizationsList
            {...organizations}
            searching={searching}
            current={current}
            onChosen={onChosen}
          />
        </li>
      ) : null}
    </>
  );
}

function PlatformTenants({
  can,
  q,
  current,
  onChosen,
  onShown,
}: {
  readonly can: PlaceCan;
  readonly q: string;
  readonly current: Place | null;
  readonly onChosen?: (() => void) | undefined;
  readonly onShown: (key: string, shown: Shown | null) => void;
}) {
  // The platform's routes are evaluated in the caller's own active organization, so they carry its
  // headers; without an active organization there is nothing to evaluate in, and the list says so.
  const active = useActiveContextOptional()?.active ?? null;
  const tenantId = active?.tenantId;
  const organizationId = active?.organizationId;
  const read = useCallback<PageRead>(
    (query) =>
      tenantId === undefined || organizationId === undefined
        ? Promise.resolve({ response: new Response(null, { status: 400 }) })
        : apiClient.GET('/v1/administration/platform/tenants', {
            params: {
              query,
              header: {
                'X-Active-Tenant-Id': tenantId,
                'X-Active-Organization-Id': organizationId,
              },
            },
          }),
    [tenantId, organizationId],
  );
  // While searching, every tenant is a candidate — an organization can match under a tenant whose
  // own name does not — so the tenants are read whole and matched here, by the rule the server
  // applies, while each tenant searches its own organizations.
  const { state, more, retry } = usePlacePage(read, '');
  const shown = platformShown(state);
  useEffect(() => {
    onShown('tenants', shown);
  }, [onShown, shown]);
  useEffect(() => () => onShown('tenants', null), [onShown]);
  return (
    <>
      {state.items.map((tenant: PlaceSummary) => (
        <TenantRow
          key={tenant.id}
          tenant={tenant}
          can={can}
          q={q}
          current={current}
          onChosen={onChosen}
          defaultOpen={current?.kind === 'organization' && state.items.length === 1}
          onShown={onShown}
        />
      ))}
      <PageFooter state={state} more={more} retry={retry} emptyKey={null} />
    </>
  );
}

function ChooserSearch({
  typed,
  onType,
  autoFocus,
}: {
  readonly typed: string;
  readonly onType: (value: string) => void;
  readonly autoFocus: boolean;
}) {
  const { t } = useTranslation('administration');
  const searchId = useId();
  return (
    <div className="relative">
      <label htmlFor={searchId} className="sr-only">
        {t('picker.searchLabel')}
      </label>
      <Search
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        id={searchId}
        type="search"
        autoFocus={autoFocus}
        value={typed}
        onChange={(event) => onType(event.target.value)}
        placeholder={t('picker.searchPlaceholder')}
        className="pl-9"
      />
    </div>
  );
}

function useScopeTree(scopes: readonly AdministrationScope[]) {
  const global = scopes.find((scope) => scope.tier === 'global');
  const tenantGrants = useMemo(() => scopes.filter((scope) => scope.tier === 'tenant'), [scopes]);
  const organizationGroups = useMemo(() => {
    const groups = new Map<string, { tenant: string; grants: AdministrationScope[] }>();
    for (const scope of scopes) {
      if (scope.tier !== 'organization' || scope.place.id === null) {
        continue;
      }
      // An organization a broader grant already lists is shown there, not twice.
      const tenantId = scope.tenant?.id ?? '';
      if (tenantGrants.some((grant) => grant.place.id === tenantId)) {
        continue;
      }
      const group = groups.get(tenantId) ?? { tenant: scope.tenant?.name ?? '', grants: [] };
      group.grants.push(scope);
      groups.set(tenantId, group);
    }
    return [...groups.values()];
  }, [scopes, tenantGrants]);
  return { global, tenantGrants, organizationGroups };
}

function useShownReports() {
  const [reports, setReports] = useState<ReadonlyMap<string, Shown>>(new Map());
  const onShown = useCallback((key: string, shown: Shown | null) => {
    setReports((now) => {
      if ((now.get(key) ?? null) === shown) {
        return now;
      }
      const next = new Map(now);
      if (shown === null) {
        next.delete(key);
      } else {
        next.set(key, shown);
      }
      return next;
    });
  }, []);
  return { reports, onShown };
}

type RowsProps = {
  readonly q: string;
  readonly current: Place | null;
  readonly onChosen?: (() => void) | undefined;
  readonly onShown: (key: string, shown: Shown | null) => void;
};

function PlatformRows({
  global,
  q,
  current,
  onChosen,
  onShown,
}: RowsProps & { readonly global: AdministrationScope }) {
  const { t } = useTranslation('administration');
  return (
    <>
      {matches(global.place.name, q) ? (
        <Row
          icon={<Globe className="size-4.5" />}
          name={global.place.name}
          sub={t('picker.platformSub')}
          href={placeBasePath({ kind: 'platform' })}
          current={current?.kind === 'platform'}
          onChosen={onChosen}
        />
      ) : null}
      <PlatformTenants
        can={global.can}
        q={q}
        current={current}
        onChosen={onChosen}
        onShown={onShown}
      />
    </>
  );
}

function GrantedRows({
  tenantGrants,
  groupsShown,
  q,
  current,
  onChosen,
  onShown,
}: RowsProps & {
  readonly tenantGrants: readonly AdministrationScope[];
  readonly groupsShown: readonly { tenant: string; grants: AdministrationScope[] }[];
}) {
  const { t } = useTranslation('administration');
  return (
    <>
      {tenantGrants
        .filter((grant) => grant.place.id !== null)
        .map((grant) => (
          <TenantRow
            key={grant.place.id}
            tenant={{ id: grant.place.id ?? '', name: grant.place.name }}
            can={grant.can}
            q={q}
            current={current}
            onChosen={onChosen}
            defaultOpen
            onShown={onShown}
          />
        ))}
      {groupsShown.map((group) => (
        <li key={group.tenant}>
          <p className="text-muted-foreground px-3 pt-3 pb-1 text-[11px] font-semibold tracking-[0.08em] uppercase">
            {group.tenant}
          </p>
          <ul className="flex flex-col gap-0.5">
            {group.grants.map((grant) => (
              <Row
                key={grant.place.id}
                icon={<Building2 className="size-4.5" />}
                name={grant.place.name}
                sub={t('picker.organizationSub')}
                href={placeBasePath({ kind: 'organization', id: grant.place.id ?? '' })}
                current={
                  current !== null &&
                  samePlace(current, { kind: 'organization', id: grant.place.id ?? '' })
                }
                onChosen={onChosen}
              />
            ))}
          </ul>
        </li>
      ))}
    </>
  );
}

/**
 * The place chooser (FR-AD-1, the approved boards): the platform, the tenants and their
 * organizations — only where the caller administers. The tree is assembled here from three
 * sources, as the contract defines them: one `/scopes` entry per grant, then each place's own
 * paged list for what lies below it. Archived organizations are not in those lists; they are
 * restored from Organizzazioni, not chosen.
 */
export function PlaceChooser({
  current,
  onChosen,
  autoFocus = false,
}: {
  readonly current: Place | null;
  readonly onChosen?: () => void;
  readonly autoFocus?: boolean;
}) {
  const { t } = useTranslation('administration');
  const { scopes } = useAdministrationScopes();
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');

  // The server searches too: wait for a pause in typing rather than asking on every key.
  useEffect(() => {
    const timer = setTimeout(() => setQ(typed.trim()), 250);
    return () => clearTimeout(timer);
  }, [typed]);

  const { global, tenantGrants, organizationGroups } = useScopeTree(scopes);

  // What the tenant rows show while searching, each read on its own: the chooser says that
  // nothing matches only once none shows anything and none is still being read.
  const { reports, onShown } = useShownReports();
  const groupsShown = organizationGroups
    .map((group) => ({
      ...group,
      grants: group.grants.filter((grant) => matches(grant.place.name, q)),
    }))
    .filter((group) => group.grants.length > 0);
  const reported = [...reports.values()];
  const nothingFound =
    q !== '' &&
    !(global !== undefined && matches(global.place.name, q)) &&
    groupsShown.length === 0 &&
    !reported.includes('shown') &&
    !reported.includes('pending');

  return (
    <div className="flex flex-col gap-2">
      <ChooserSearch typed={typed} onType={setTyped} autoFocus={autoFocus} />
      <ul className="flex flex-col gap-0.5" aria-label={t('picker.title')}>
        {global !== undefined ? (
          <PlatformRows
            global={global}
            q={q}
            current={current}
            onChosen={onChosen}
            onShown={onShown}
          />
        ) : (
          <GrantedRows
            tenantGrants={tenantGrants}
            groupsShown={groupsShown}
            q={q}
            current={current}
            onChosen={onChosen}
            onShown={onShown}
          />
        )}
        {nothingFound ? (
          <li className="text-muted-foreground px-3 py-2 text-[13px]">
            {t('picker.empty', { query: q })}
          </li>
        ) : null}
      </ul>
      <p className="text-muted-foreground border-border border-t px-1 pt-2 text-[12.5px]">
        {t('picker.footer')}
      </p>
    </div>
  );
}
